<script setup>
import { Nav } from '@/components';
import { Alert, InfoBubble, CartInfo, LoginSyncOverlay, UploadModal, MediaModal, UploadButton } from '@lib/pp-shop-lib/components';
import { useAuthStore, useConfigStore, useSessionStore } from '@lib/pp-shop-lib/stores';
import { ProductSelectorModal } from '@lib/pp-shop-lib/components';
import { DeveloperConsole } from '@lib/pp-shop-lib/components/debug';
import { Mercure, MercureEvents } from '@lib/pp-shop-lib/classes';
import { dispatchPpShopEvent, glob, logger } from '@lib/pp-shop-lib/helpers';
import { storeToRefs } from 'pinia';
import { onMounted, watch } from 'vue';


const authStore = useAuthStore();
const configStore = useConfigStore();
const sessionStore = useSessionStore();

sessionStore.normalizeCart()
let wasLoggedIn = Boolean(authStore.user?.kid)


// Dom checker. Element muss für Teleport beim Laden verfügbar sein
// Wird eigentlich von vue geprüft, führt aber zu Fehlern
function existsInDom(element) {
    return document.querySelector(element)
}

const { user } = storeToRefs(authStore);

onMounted(() => {
    const currentKid = authStore.user?.kid
    logger.info('[App] current KID', currentKid)
    if (currentKid) {
        document.body.classList.add('is-authenticated')

        sessionStore.syncCartFromServer(currentKid, { authoritative: true }).catch((error) => {
            logger.warn('[App] initial cart sync failed', error)
        })
    }

})

let mercureEvents = glob("mercureEvents", MercureEvents)
const mercure = new Mercure()
let reconnectChain = Promise.resolve()

glob("mercure",mercure)

function reconnectMercure(reason) {
    reconnectChain = reconnectChain
        .catch(() => undefined)
        .then(async () => {
            const kid = user.value?.kid || 0
            const sessionId = authStore.setup?.sessionId || ''
            logger.info(`Mercure reconnect (${reason}). KID ${kid}, SID ${sessionId || '-'}`)

            await mercure.connect(1)

            if (mercureEvents && typeof mercureEvents.close === 'function') {
                mercureEvents.close()
            }

            // Immer neue Instanz erzeugen, damit Topics (kid vs. sessionId)
            // zum aktuellen Auth-Status passen.
            mercureEvents = glob("mercureEvents", MercureEvents, false)
            await mercureEvents.listen()

            // Signalisiert Komponenten mit bestehenden Manager-Instanzen,
            // dass die globale MercureEvents-Instanz ausgetauscht wurde.
            dispatchPpShopEvent('pp-shop:mercure-events-recreated')
        })
        .catch((error) => {
            logger.error('Mercure reconnect failed', error)
        })

    return reconnectChain
}

authStore.setClientName()
authStore.expiryListener()

// Bei KID- oder Session-Wechsel immer deterministisch neu verbinden.
watch(
    () => [user.value?.kid || 0, authStore.setup?.sessionId || ''],
    ([kid, sessionId]) => {
        const isLoggedIn = Boolean(kid)

        // Gastmodus: Cart ist lokal und an die aktuelle Session-ID gebunden.
        // Beim Logout (neue Session-ID) oder Sessionwechsel muss der alte Cart verworfen werden.
        if (!isLoggedIn) {
            const cartReset = sessionStore.enforceGuestCartSession(sessionId, wasLoggedIn)
            if (cartReset) {
                logger.info(`Cart reset after logout/session-change (SID ${sessionId || '-'})`)
            }
            document.body.classList.remove('is-authenticated')
        } else {
            document.body.classList.add('is-authenticated')
        }

        wasLoggedIn = isLoggedIn
        reconnectMercure('auth-or-session-change')
    },
    { immediate: true }
)

function shouldRenderUploadTeleport(ub) {
    const target = existsInDom(ub.element)
    logger.info('[uploadButton teleport]', {
        ub,
        element: ub.element,
        check: ub.check,
        target,
        shouldRender: ub.check === false || Boolean(target),
    })
    return ub.check === false || Boolean(target)
}

</script>

<template>
    <Teleport to="#pp-alert">
        <Alert />
    </Teleport>
    <Teleport to="#pp-shop">
        <div class="app-container" :class="authStore.user && 'bg-light'">
            <Nav v-if="configStore.globalNav" />
            <div class="uk-container">
                <router-view />
            </div>
            <DeveloperConsole></DeveloperConsole>
            <ProductSelectorModal></ProductSelectorModal>
        </div>
    </Teleport>
    <div v-for="(cb, index) in configStore.cartBubble" :key="index">
        <Teleport v-if="cb.check === false || existsInDom(cb.element)" :to="cb.element">
            <InfoBubble info="cart"></InfoBubble>
        </Teleport>
    </div>
    <div v-for="(ab, index) in configStore.accountBubble" :key="`account-bubble-${index}`">
        <Teleport v-if="ab.check === false || existsInDom(ab.element)" :to="ab.element">
            <InfoBubble info="account"></InfoBubble>
        </Teleport>
    </div>
    <div v-for="(ci, index) in configStore.cartInfo" :key="index">
        <Teleport v-if="ci.check === false || existsInDom(ci.element)" :to="ci.element">
            <CartInfo></CartInfo>
        </Teleport>
    </div>
    <div v-for="(ub, index) in configStore.uploadButton" :key="index">
        <Teleport v-if="shouldRenderUploadTeleport(ub)" :to="ub.element">
            <UploadButton class="pdf-upload-button" :variant="ub.variant" :global-function="ub.globalFunction" :redirect-path="ub.redirectPath" :redirect-method="ub.redirectMethod" :upload-meta="ub.uploadMeta"></UploadButton>
        </Teleport>
    </div>
    <UploadModal></UploadModal>
    <MediaModal></MediaModal>
    <LoginSyncOverlay></LoginSyncOverlay>

</template>

<style>
@import '@/assets/step-progress.css';
</style>
